import React, {useEffect, useState} from 'react';
import {AppContainer} from "../domain/AppContainer";
import './LeaguePlayersPrintView.css';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faSquare} from "@fortawesome/free-regular-svg-icons/faSquare";
import {useObservable, useObservableWithInitialValue} from "../hooks/useEntityStore";
import {useLocation, useParams} from "react-router-dom";
import QRCode from "react-qr-code";
import {ajax} from "rxjs/ajax";
import {applyActivityFilter, applyEventFilter, applyRatingFilter, sortPlayers, formatLastPlayed, SortColumn, SortDir} from "../components/PlayerTable";
import {ListPlayers} from "../api/endpoints";
import {ResponseWrapper} from "../api/types";

interface Props {
    container: AppContainer
}

interface Params {
    leagueId: string
}

export function LeaguePlayersPrintView({container}: Props) {
    let {leagueId} = useParams<Params>();
    const {search} = useLocation();
    const allPlayers = useObservableWithInitialValue(container.player.query.selectAll(), [])
    const ready = useObservable(container.player.query.ready$)

    const params = new URLSearchParams(search);
    const activityDays = params.get('activity') ? parseInt(params.get('activity')!, 10) : null;
    const sortCol = (params.get('sortCol') ?? 'rating') as SortColumn;
    const sortDir = (params.get('sortDir') ?? 'desc') as SortDir;
    const ratingFilter = params.get('ratingFilter') ?? 'All Ratings';
    const neverPlayedOnly = params.get('neverPlayed') === 'true';
    const eventName = params.get('eventName') ?? '';

    const [eventPlayerIds, setEventPlayerIds] = useState<Set<number> | null>(null);

    const players = sortPlayers(
        applyRatingFilter(
            applyActivityFilter(
                applyEventFilter(allPlayers, eventPlayerIds),
                activityDays,
                neverPlayedOnly
            ),
            ratingFilter
        ),
        sortCol,
        sortDir
    );
    const half = Math.ceil(players.length / 2);
    const firstHalf = players.slice(0, half);
    const secondHalf = players.slice(half);

    useEffect(() => {
        container.initLeague(leagueId)
    }, [leagueId, container])

    useEffect(() => {
        if (!eventName || !leagueId) return;
        const sub = ajax.getJSON<ResponseWrapper<{players: {id: number}[]}>>(`${ListPlayers(leagueId)}?eventName=${encodeURIComponent(eventName)}`)
            .subscribe({
                next: r => setEventPlayerIds(new Set((r.data?.players ?? []).map(p => p.id))),
                error: () => {}
            });
        return () => sub.unsubscribe();
    }, [leagueId, eventName]);

    const printReady = ready && (!eventName || eventPlayerIds !== null);

    useEffect(() => {
        if (printReady) {
            window.print();
        }
    }, [printReady])

    return <>
        <header className="print-view-header">
            <div>See it online</div>
            <QRCode value={window.location.href.replace('/print', '')} level={"H"} size={64}/>
            <div>{window.location.href.replace('/print', '')}</div>
        </header>
        <div className="league-players-print-view">

            <table>
                <thead>
                <tr>
                    <th className="empty-header"/>
                    <th className="align-left">First Name</th>
                    <th className="align-left">Last Name</th>
                    <th className="align-right">Rating</th>
                    <th className="align-right">Last Played</th>
                </tr>
                </thead>
                <tbody>
                {firstHalf.map((p, i) =>
                    <tr key={i}>
                        <td className="align-right checkbox"><FontAwesomeIcon icon={faSquare}/></td>
                        <td className="align-left">{p.firstName}</td>
                        <td className="align-left">{p.lastName}</td>
                        <td className="align-right">{p.rating}</td>
                        <td className="align-right">{formatLastPlayed(p.lastPlayedAt)}</td>
                    </tr>
                )}
                </tbody>
            </table>
            <table>
                <thead>
                <tr>
                    <th className="empty-header"/>
                    <th className="align-left">First Name</th>
                    <th className="align-left">Last Name</th>
                    <th className="align-right">Rating</th>
                    <th className="align-right">Last Played</th>
                </tr>
                </thead>
                <tbody>
                {secondHalf.map((p, i) =>
                    <tr key={i}>
                        <td className="align-right checkbox"><FontAwesomeIcon icon={faSquare}/></td>
                        <td className="align-left">{p.firstName}</td>
                        <td className="align-left">{p.lastName}</td>
                        <td className="align-right">{p.rating}</td>
                        <td className="align-right">{formatLastPlayed(p.lastPlayedAt)}</td>
                    </tr>
                )}
                </tbody>
            </table>
        </div>
    </>
}
